import { Calendar, Clock, Globe } from "@tamagui/lucide-icons";
import { useHubs } from "common";
import { FormikProvider, useFormik } from "formik";
import { useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { Heading, Text, XStack, YStack } from "tamagui";
import { GradientButton } from "tamagui-config";

import { DividerLine } from "../../components/DividerLine";
import { FormikInput } from "../../components/FormikInput";
import { FormikSelect } from "../../components/FormikSelect";
import { SelectOption } from "../../components/Select";
import { useAppSync } from "../../contexts/AppSyncContext";
import useFieldValidationSchema from "../../hooks/useFieldValidationSchema";

import "react-datepicker/dist/react-datepicker.css";

const timeOptions: SelectOption[] = [
  { label: "12:00 AM", value: "00:00" },
  { label: "01:00 AM", value: "01:00" },
  { label: "02:00 AM", value: "02:00" },
  { label: "03:00 AM", value: "03:00" },
  { label: "04:00 AM", value: "04:00" },
  { label: "05:00 AM", value: "05:00" },
  { label: "06:00 AM", value: "06:00" },
  { label: "07:00 AM", value: "07:00" },
  { label: "08:00 AM", value: "08:00" },
  { label: "09:00 AM", value: "09:00" },
  { label: "10:00 AM", value: "10:00" },
  { label: "11:00 AM", value: "11:00" },
  { label: "12:00 PM", value: "12:00" },
  { label: "01:00 PM", value: "13:00" },
  { label: "02:00 PM", value: "14:00" },
  { label: "03:00 PM", value: "15:00" },
  { label: "04:00 PM", value: "16:00" },
  { label: "05:00 PM", value: "17:00" },
  { label: "06:00 PM", value: "18:00" },
  { label: "07:00 PM", value: "19:00" },
  { label: "08:00 PM", value: "20:00" },
  { label: "09:00 PM", value: "21:00" },
  { label: "10:00 PM", value: "22:00" },
  { label: "11:00 PM", value: "23:00" },
];

const timezoneOptions: SelectOption[] = [
  { label: "Los Angeles, CA (GMT-7)", value: "America/Los_Angeles" },
  { label: "Denver, CO (GMT-6)", value: "America/Denver" },
  { label: "Chicago, IL (GMT-5)", value: "America/Chicago" },
  { label: "New York, NY (GMT-4)", value: "America/New_York" },
  { label: "UTC (GMT+0)", value: "Etc/UTC" },
  { label: "London, UK (GMT+1)", value: "Europe/London" },
  { label: "Berlin, DE (GMT+2)", value: "Europe/Berlin" },
  { label: "Kyiv, UA (GMT+3)", value: "Europe/Kyiv" },
  { label: "Dubai, AE (GMT+4)", value: "Asia/Dubai" },
  { label: "Mumbai, IN (GMT+5:30)", value: "Asia/Kolkata" },
  { label: "Bangkok, TH (GMT+7)", value: "Asia/Bangkok" },
  { label: "Hong Kong, HK (GMT+8)", value: "Asia/Hong_Kong" },
  { label: "Tokyo, JP (GMT+9)", value: "Asia/Tokyo" },
  { label: "Sydney, AU (GMT+10)", value: "Australia/Sydney" },
  { label: "Auckland, NZ (GMT+12)", value: "Pacific/Auckland" },
];

const stateOptions: SelectOption[] = [
  { label: "Alabama (AL)", value: "AL" },
  { label: "Alaska (AK)", value: "AK" },
  { label: "Arizona (AZ)", value: "AZ" },
  { label: "Arkansas (AR)", value: "AR" },
  { label: "California (CA)", value: "CA" },
  { label: "Colorado (CO)", value: "CO" },
  { label: "Connecticut (CT)", value: "CT" },
  { label: "Delaware (DE)", value: "DE" },
  { label: "Florida (FL)", value: "FL" },
  { label: "Georgia (GA)", value: "GA" },
  { label: "Hawaii (HI)", value: "HI" },
  { label: "Idaho (ID)", value: "ID" },
  { label: "Illinois (IL)", value: "IL" },
  { label: "Indiana (IN)", value: "IN" },
  { label: "Iowa (IA)", value: "IA" },
  { label: "Kansas (KS)", value: "KS" },
  { label: "Kentucky (KY)", value: "KY" },
  { label: "Louisiana (LA)", value: "LA" },
  { label: "Maine (ME)", value: "ME" },
  { label: "Maryland (MD)", value: "MD" },
  { label: "Massachusetts (MA)", value: "MA" },
  { label: "Michigan (MI)", value: "MI" },
  { label: "Minnesota (MN)", value: "MN" },
  { label: "Mississippi (MS)", value: "MS" },
  { label: "Missouri (MO)", value: "MO" },
  { label: "Montana (MT)", value: "MT" },
  { label: "Nebraska (NE)", value: "NE" },
  { label: "Nevada (NV)", value: "NV" },
  { label: "New Hampshire (NH)", value: "NH" },
  { label: "New Jersey (NJ)", value: "NJ" },
  { label: "New Mexico (NM)", value: "NM" },
  { label: "New York (NY)", value: "NY" },
  { label: "North Carolina (NC)", value: "NC" },
  { label: "North Dakota (ND)", value: "ND" },
  { label: "Ohio (OH)", value: "OH" },
  { label: "Oklahoma (OK)", value: "OK" },
  { label: "Oregon (OR)", value: "OR" },
  { label: "Pennsylvania (PA)", value: "PA" },
  { label: "Rhode Island (RI)", value: "RI" },
  { label: "South Carolina (SC)", value: "SC" },
  { label: "South Dakota (SD)", value: "SD" },
  { label: "Tennessee (TN)", value: "TN" },
  { label: "Texas (TX)", value: "TX" },
  { label: "Utah (UT)", value: "UT" },
  { label: "Vermont (VT)", value: "VT" },
  { label: "Virginia (VA)", value: "VA" },
  { label: "Washington (WA)", value: "WA" },
  { label: "West Virginia (WV)", value: "WV" },
  { label: "Wisconsin (WI)", value: "WI" },
  { label: "Wyoming (WY)", value: "WY" },
];

export const GeneralSettings = () => {
  const { generalSettingsValidationSchema } = useFieldValidationSchema();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();
  const { updateHubAddress, fetchHubs } = useHubs({
    fetchAndSubscribeOnLoad: true,
  });
  const { selectedHub } = useAppSync();

  const handleGeneralSettingsSubmit = async (values: any) => {
    if (!selectedHub) return;
    setIsSaving(true);

    try {
      await updateHubAddress({
        id: selectedHub.id,
        street: values.street,
        city: values.city,
        state: values.state,
        zip: values.zip,
        date: values.date,
        time: values.time,
        timezone: values.timezone,
      });

      if (values.date || values.time || values.timezone) {
        const { updateHubDateTime } = await import(
          "../../services/dateTimeApi"
        );
        await updateHubDateTime({
          thingName: selectedHub.id,
          date: values.date || undefined,
          time: values.time || undefined,
          timezone: values.timezone || undefined,
        });
      }

      fetchHubs();
    } catch (error) {
      console.error("Failed to update settings:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      street: selectedHub?.street || "",
      city: selectedHub?.city || "",
      state: selectedHub?.state || "",
      zip: selectedHub?.zip || "",
      date: selectedHub?.date || "",
      time: selectedHub?.time || "",
      timezone: selectedHub?.timezone || "",
    },
    enableReinitialize: true,
    validationSchema: generalSettingsValidationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: handleGeneralSettingsSubmit,
  });

  // These debug useEffects have been removed

  return (
    <YStack gap="$4">
      <YStack gap="$2">
        <Text fontSize="$5.5">{t("sidebar.settings.settings")}</Text>
        <Heading fontSize="$8">{t("sidebar.settings.general")}</Heading>
      </YStack>
      <YStack
        key={selectedHub?.id || "no-hub"}
        gap="$3"
        backgroundColor="$transparentBorder"
        padding="$4"
        borderRadius="$4"
      >
        <FormikProvider value={formik}>
          <YStack gap="$4">
            <YStack width="350px" gap="16px">
              <Heading fontSize="20px" lineHeight="$2">
                {t("generalSettings.baseStationAddress")}
              </Heading>
              <YStack gap="12px">
                <Heading fontSize="18px" lineHeight="$2">
                  {t("generalSettings.street")}
                </Heading>
                <FormikInput
                  fieldName="street"
                  backgroundColor="$gray2"
                  placeholder={t("generalSettings.streetPlaceholder")}
                />
              </YStack>
              <YStack gap="12px">
                <Heading fontSize="18px" lineHeight="$2">
                  {t("generalSettings.city")}
                </Heading>
                <FormikInput
                  fieldName="city"
                  backgroundColor="$gray2"
                  placeholder={t("generalSettings.cityPlaceholder")}
                />
              </YStack>
              <XStack gap="$3">
                <YStack gap="12px">
                  <Heading fontSize="18px" lineHeight="$2">
                    {t("generalSettings.state")}
                  </Heading>
                  <FormikSelect
                    options={stateOptions}
                    placeholder={t("generalSettings.statePlaceholder")}
                    special
                    fieldName="state"
                  />
                </YStack>
                <YStack gap="12px">
                  <Heading fontSize="18px" lineHeight="$2">
                    {t("generalSettings.zip")}
                  </Heading>
                  <FormikInput
                    fieldName="zip"
                    backgroundColor="$gray2"
                    height="$4.5"
                    placeholder={t("generalSettings.zipPlaceholder")}
                  />
                </YStack>
              </XStack>
            </YStack>
            <DividerLine />
            <YStack width="350px">
              <Heading fontSize="$3" lineHeight="$3" paddingVertical="$3">
                {t("sidebar.settings.settings")}
              </Heading>
              <XStack gap="$2" paddingBottom="12px">
                <Calendar />
                <Heading fontSize="18px" lineHeight="$2">
                  {t("generalSettings.date")}
                </Heading>
              </XStack>
              <DatePicker
                selected={
                  formik.values.date ? new Date(formik.values.date) : null
                }
                onChange={(date) => {
                  formik.setFieldValue("date", date ? date.toISOString() : "");
                  setIsDatePickerOpen(false);
                }}
                onBlur={() => {
                  formik.setFieldTouched("date", true);
                  setIsDatePickerOpen(false);
                }}
                onClickOutside={() => setIsDatePickerOpen(false)}
                onSelect={() => setIsDatePickerOpen(false)}
                open={isDatePickerOpen}
                onInputClick={() => setIsDatePickerOpen(true)}
                dateFormat="yyyy-MM-dd"
                placeholderText={t("generalSettings.datePlaceholder")}
              />
              {formik.touched.date && formik.errors.date && (
                <Text color="$red10" fontSize={13}>
                  {formik.errors.date}
                </Text>
              )}
            </YStack>
            <YStack gap="12px" width="350px">
              <XStack gap="$2">
                <Clock />
                <Heading fontSize="18px" lineHeight="$2">
                  {t("generalSettings.time")}
                </Heading>
              </XStack>
              <FormikSelect
                options={timeOptions}
                placeholder={t("generalSettings.timePlaceholder")}
                special
                fieldName="time"
              />
            </YStack>
            <YStack gap="12px" width="350px">
              <XStack gap="$2">
                <Globe />
                <Heading fontSize="18px" lineHeight="$2">
                  {t("generalSettings.timezone")}
                </Heading>
              </XStack>
              <FormikSelect
                options={timezoneOptions}
                placeholder={t("generalSettings.timezonePlaceholder")}
                special
                fieldName="timezone"
              />
            </YStack>
          </YStack>

          <GradientButton
            rounded
            width="100px"
            loading={isSaving}
            onPress={async () => {
              formik.setTouched({
                street: true,
                city: true,
                state: true,
                zip: true,
                date: true,
                time: true,
                timezone: true,
              });
              const valid = await formik.validateForm();
              if (Object.keys(valid).length === 0) {
                formik.handleSubmit();
              }
            }}
          >
            <Text fontSize="$3">{t("generalSettings.save")}</Text>
          </GradientButton>
        </FormikProvider>
      </YStack>
    </YStack>
  );
};
export default GeneralSettings;
